import React from 'react';

import { Periodicity, PeriodicityKey } from '../time-period-selector/PeriodsTypes';

/* eslint-disable-next-line */
export interface FilteredPeriodProps {
  selectedPeriod: string;
  comparisonPeriod: string;
  selectedPeriodicity?: Periodicity;
  isDisplayNoComparisonLabel?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FilteredPeriod: React.FC<FilteredPeriodProps> = ({
  selectedPeriod,
  comparisonPeriod,
  selectedPeriodicity,
  isDisplayNoComparisonLabel = false,
}) => {
  let displayText = `${selectedPeriod} vs ${comparisonPeriod}`;

  if (selectedPeriodicity === Periodicity.Weekly) {
    displayText = `${selectedPeriod} vs Week ${comparisonPeriod}`;
  }

  // formatting for YTD and MAT to slice first 4 char, as formatting from filter API
  // is YTD MMM yyyy - MMM yyyy (Monthly) or YTD Week MM yyyy - Week MM yyyy
  // same with MAT, otherwise display period as it is
  if (selectedPeriod.includes(PeriodicityKey.YTD) || selectedPeriod.includes(PeriodicityKey.MAT)) {
    comparisonPeriod = checkPeriodicity(selectedPeriod);
    selectedPeriod = selectedPeriod.slice(4);
    displayText = `${selectedPeriod} vs ${comparisonPeriod}`;
  }

  if (isDisplayNoComparisonLabel) {
    // Up till this point, displayText would always be initialised in the following form:
    // <current period> vs <comparison period>
    // With this assumption, we can safely split the string by ' vs ' and take the first part
    const focusPeriod = displayText.split(' vs ')[0];
    displayText = `${focusPeriod} vs No Comparison`;
  }

  if (selectedPeriod === '') {
    displayText = '';
  }
  return (
    <div className="text-lighter text-sm font-bold inline-block pr-1" data-test-id="filtered-period">
      {displayText}
    </div>
  );
};

/**
 * check if selected period is weekly or monthly period, then form comparison period
 * @param selectedPeriod
 * e.g. Jan 2022 - Jun 2022 (Monthly period)
 * e.g. Week 1 2022 - Week 32 2022 (Weekly period)
 */
export function checkPeriodicity(selectedPeriod: string) {
  return selectedPeriod.toUpperCase().includes('WEEK')
    ? getYTDorMATComparisonPeriodWeekly(selectedPeriod)
    : getYTDorMATComparisonPeriodMonthly(selectedPeriod);
}

/**
 * get monthly comparison period for YTD and MAT
 * @param selectedPeriod
 * e.g. YTD Jan 2022 - Jun 2022 (Monthly period)
 * @return monthly comparison period
 * e.g. Jan 2021 - Jun 2021 (Monthly period)
 */
function getYTDorMATComparisonPeriodMonthly(selectedPeriod: string) {
  const monthYear = selectedPeriod.split(' ').slice(1);
  const previousInitialMonth = monthYear[0];
  const previousInitialYear = Number(monthYear[1]) - 1;
  const previousEndMonth = monthYear[3];
  const previousEndYear = Number(monthYear[4]) - 1;
  return `${previousInitialMonth} ${previousInitialYear} - ${previousEndMonth} ${previousEndYear}`;
}

/**
 * get weekly comparison period for YTD and MAT
 * @param selectedPeriod
 * e.g. YTD Week 1 2022 - Week 32 2022 (Weekly period)
 * @return
 * e.g. Week 1 2021 - Week 32 2021 (Weekly period)
 */
function getYTDorMATComparisonPeriodWeekly(selectedPeriod: string) {
  const monthYear = selectedPeriod.split(' ').slice(1);
  const previousInitialWeek = `${monthYear[0]} ${monthYear[1]}`;
  const previousInitialYear = Number(monthYear[2]) - 1;
  const previousEndWeek = `${monthYear[4]} ${monthYear[5]}`;
  const previousEndYear = Number(monthYear[6]) - 1;
  return `${previousInitialWeek} ${previousInitialYear} - ${previousEndWeek} ${previousEndYear}`;
}

export default FilteredPeriod;
